"use client";
import React, { useState, useEffect } from "react";
import { Button, TextField } from "@mui/material";
import { useTranslation } from "@/i18n/client";
import { observer } from "mobx-react-lite";
// Store
import { useStores } from "@/models";
// -------------- api -------------------- //
import { fetchFooterMenu, fetchSubmitEmail } from "@/common-components-src/api/footer/footer.api";
import { SceneFacadeVO, FooterMenuVo } from "@/common-components-src/api/footer/footer.interface";
import { Country, Language, Currency } from "@/apis/address/address.interface";
// -------------- 组件 -------------------- //
import UKToast from "@/components/UKToast";
import CookiesManage from "./cookiesManage";
import PersonalManage from "./personalManage";
import ContactInfoValidUtil from "@/utils/contactInfoValidTool";
// -------------- 图标 -------------------- //
import LangSVG from "@/assets/icons/lang.svg";
import LogoSVG from "@/assets/icons/newLogoRed.svg";
import BrandFacebook from "@/assets/icons/brand-facebook.svg";
import BrandInstagram from "@/assets/icons/brand-instagram.svg";
import BrandYoutube from "@/assets/icons/brand-youtube.svg";
import BrandTiktok from "@/assets/icons/brand-tiktok.svg";
import BrandLinkTree from "@/assets/icons/brand-linktree.svg";
// -------------- styles -------------------- //
import styles from "./index.module.scss";
// -------------- types, constants, interface -------------------- //
import FooterData, { getBasicData } from "@/common-components-src/const/footer";
import cookieUtils from "@/utils/cookieUtils";
import { queryGlobalCountriesData } from "@/utils/countryUtils";
// -------------- pv -------------------- //
import { EXPOSURE_RECORD, sendClickTrack } from "@/tracks/25421";

interface FooterVo {
  title: string;
  url?: string;
  urlName: string;
  contactMethod?: string;
}

const JDIFooter = observer(() => {
  const { t } = useTranslation();
  const { countryStore } = useStores();
  const { AboutBasicList, LegalBasicList, ContactBasicList } = getBasicData(t);
  const { Instagram, Tiktok, Facebook, Youtube, Linktree, googleDownload, AppDownload } = FooterData;
  const [aboutList, setAboutList] = useState<FooterVo[]>(AboutBasicList); // Get to know us
  const [legalList, setLegalList] = useState<FooterVo[]>(LegalBasicList); // Legal term & policies
  const [contactList, setContactList] = useState<FooterVo[]>(ContactBasicList); // Let us help you
  const [emailValue, setEmailValue] = useState<string>(""); // email
  const [error, setError] = useState<string>("");
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [openManager, setOpenManager] = useState<boolean>(false);
  const [openCookiesManage, setOpenCookiesManage] = useState<boolean>(false);
  const [openPersonalManage, setOpenPersonalManage] = useState<boolean>(false);

  // 语言、货币、国家相关状态
  const [currentLanguage, setCurrentLanguage] = useState<string>(); // 当前语言
  const [currentCurrency, setCurrentCurrency] = useState<string>(); // 当前货币
  const [currentCountryData, setCurrentCountryData] = useState<Country | undefined>(); // 当前国家数据

  useEffect(() => {
    getFooterMenu();
  }, []);

  useEffect(() => {
    setOpenCookiesManage(openDialog || openManager);
  }, [openDialog, openManager]);

  useEffect(() => {
    const cookieConsent = cookieUtils.getCookie("cookie_consent");
    const isShowCookiesManage = cookieUtils.getSessionStorage("isShowCookiesManage") || 1;
    setOpenManager(!Boolean(cookieConsent) && !!Number(isShowCookiesManage));
  }, []);

  useEffect(() => {
    if (countryStore.currentCountryData) {
      setCurrentCountryData(countryStore.currentCountryData);
    } else {
      queryGlobalCountriesData()
      .then((res) => {
        const countryCode = res.currentCountryCode;
        const translationKey = `common.countryName.${countryCode}`;
        const countryName = t(translationKey);
        
        // 检查转译
        if (res.currentCountry && countryName && countryName !== translationKey) {
          setCurrentCountryData({
            ...res.currentCountry,
            name: countryName
          })
        }
      });
    }
  },[countryStore.currentCountryData])

  useEffect(() => {
    if (countryStore.currentLanguageData && countryStore.currentLanguageData.displayText) {
      setCurrentLanguage(countryStore.currentLanguageData.displayText);
    }
  }, [countryStore.currentLanguageData])

  useEffect(() => {
    if (countryStore.currentCurrencyData && countryStore.currentCurrencyData.displayText) {
      setCurrentCurrency(countryStore.currentCurrencyData.displayText);
    }
  }, [countryStore.currentCurrencyData])


  const getFooterMenu = async () => {
    try {
      const { data } = await fetchFooterMenu({ sceneList: ["About", "Legal", "Contact"] });
      const aboutRes: FooterMenuVo[] = [];
      const legalRes: FooterMenuVo[] = [];
      const contactRes: FooterMenuVo[] = [];

      if (data?.length) {
        data.forEach((item) => {
          if (item.scene === "About") {
            aboutRes.push(item);
          } else if (item.scene === "Legal") {
            legalRes.push(item);
          } else if (item.scene === "Contact") {
            contactRes.push(item);
          }
        });
        if (aboutRes.length) {
          const sceneFacadeVOList = aboutRes[0].sceneFacadeVOList || [];
          const list = sceneFacadeVOList.map((item: SceneFacadeVO) => ({
            id: item.id,
            title: item.facadeName,
            urlName: item.urlName,
            url: `/discover/${item.urlName}`,
          }));
          setAboutList(list.splice(0, 4));
        }
        if (legalRes.length) {
          const sceneFacadeVOList = legalRes[0].sceneFacadeVOList || [];
          const list = sceneFacadeVOList.map((item: SceneFacadeVO) => ({
            id: item.id,
            title: item.facadeName,
            urlName: item.urlName,
            url: `/help/${item.urlName}`,
          }));
          setLegalList(list.splice(0, 4));
        }
        if (contactRes.length) {
          const sceneFacadeVOList = contactRes[0].sceneFacadeVOList || [];
          const list = sceneFacadeVOList.map((item: SceneFacadeVO) => ({
            id: item.id,
            title: item.facadeName,
            urlName: item.urlName,
            contactMethod: item.facadeContent,
          }));
          setContactList(list);
        }
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleSubmitEmail = async () => {
    sendClickTrack.Footer_Email_Subscription();

    if (!emailValue || !ContactInfoValidUtil.isEmailPattern(emailValue)) {
      setError(t("footer.footerAlert"));
    } else {
      const res = await fetchSubmitEmail({ email: emailValue });
      if (res.code === "200") {
        UKToast.show({
          icon: "success",
          content: t("footer.submitSuccess"),
        });
        setEmailValue("");
      } else {
        UKToast.show({
          icon: "error",
          content: res.message || t("footer.submitFail"),
        });
      }
    }
  };

  // 处理输入框变化
  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setEmailValue(value);
    // 实时校验邮箱格式
    if (value && ContactInfoValidUtil.isEmailPattern(value)) {
      setError("");
    } else {
      setError(t("footer.footerAlert"));
    }
  };

  const handleOpenPersonal = () => {
    setOpenPersonalManage(true);
  };

  const handleOpenCookies = () => {
    setOpenDialog(true);
  };

  const isSamePath = (type: string) => {
    const pathname = window.location.pathname;
    if (pathname !== "/settings/customise") return false;

    try {
      const params = new URLSearchParams(window.location.search);
      const typeParam = params.get("type");

      return typeParam === type;
    } catch (error) {
      console.error("解析URL参数异常:", error);
      return false;
    }
  };

  const handleGoChange = (type: string) => {
    const flag = isSamePath(type);
    if (flag) return;

    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.delete("returnUrl");
    const returnUrl = encodeURIComponent(currentUrl.toString());
    window.location.href = `/settings/customise?type=${type}&returnUrl=${returnUrl}`;
  };

  const footerClass = `${styles["jdi-footer"]}`;

  return (
    <footer className={footerClass}>
      <div className={`${styles["footer-top-wrap"]}`}>
        <div className={styles.w}>
          <div className={styles["footer-top"]}>
            <div className={styles["left-box"]}>
              <LogoSVG color="#cc0c1c" width="102" height="28" />
              <span className={styles.slogan}>{`Don't just buy, Joybuy`}</span>
            </div>
            <div className={styles["international-wrap"]}>
              {currentLanguage && (
                <div className={styles.item} onClick={() => handleGoChange("1")}>
                  <div className={styles.ico}>
                    <LangSVG />
                  </div>
                  <div>{currentLanguage}</div>
                </div>
              )}
              {currentCurrency && (
                <div className={styles.item} onClick={() => handleGoChange("1")}>
                  <div className={styles.ico}>
                  </div>
                  <div>{currentCurrency}</div>
                </div>
              )}
              {currentCountryData?.name && (
                <div className={styles.item} onClick={() => handleGoChange("2")}>
                  {currentCountryData?.flagImage && (
                    <img src={currentCountryData.flagImage} alt="" className={styles.ico} width="20" height="14" />
                  )}
                  <div>{currentCountryData?.name}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className={styles["footer-main-wrap"]}>
        <div className={styles.w}>
          <div className={styles["footer-main"]}>
            <div className={styles["left-container"]}>
              <div className={styles.vertical}>
                <div className={styles.title}>{t("footer.aboutUs")}</div>
                <ul className={styles.links}>
                  {aboutList.map((item) => (
                    <li key={item.urlName}>
                      <a href={item.url} target="_self">
                        {item.title}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
              <div className={styles.vertical}>
                <div className={styles.title}>{t("footer.legal")}</div>
                <ul className={styles.links}>
                  {legalList.map((item) => (
                    <li key={item.urlName}>
                      <a href={item.url} target="_self">
                        {item.title}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
              <div className={styles.vertical}>
                <div className={styles.title}>{t("footer.help")}</div>
                <ul className={styles.links}>
                  <li>
                    <a href={t("static.footer.zendesk")} target="_self">
                      {t("footer.faq")}
                    </a>
                  </li>
                  {contactList.map((item) => (
                    <li key={item.urlName} className={styles["contact"]}>
                      {item.title}:<div dangerouslySetInnerHTML={{ __html: item.contactMethod }}></div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className={styles["right-container"]}>
              <div className={styles.title}> {t("footer.subscribe")}</div>
              <div
                className={styles.email}
                exptag={"exp|" + EXPOSURE_RECORD.Footer_Email_Subscription_Expo}
                data-exptag-config='{"useClick": false, "stay_duration": 0, "repeated":false, "area_rate": 0.01}'
              >
                <TextField
                  className={styles["email-input"]}
                  label={t("footer.subscribePlaceholder")}
                  variant="outlined"
                  size="small"
                  autoComplete="subscribe-email"
                  value={emailValue}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&:focus-within fieldset": {
                        borderColor: "#D32F2F2", // 当输入框获得焦点时边框颜色变为红色
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#D32F2F2",
                    },
                    "& .MuiFormHelperText-filled": {
                      backgroundColor: "#f5f6fa",
                      margin: 0,
                      padding: "4px 14px 0px",
                    },
                  }}
                  onChange={handleEmailChange}
                  error={!!error}
                  helperText={error}
                  inputProps={{ maxLength: 100 }}
                />
                <Button
                  size="small"
                  variant="contained"
                  disableElevation
                  sx={{
                    color: "#FFF",
                    height: "40px",
                    backgroundColor: "#000",
                    "&:hover": {
                      backgroundColor: "#333",
                    },
                    "&:active": {
                      backgroundColor: "#222",
                    },
                    textTransform: "none",
                  }}
                  onClick={handleSubmitEmail}
                >
                  {t("footer.submit")}
                </Button>
              </div>
              <div className={styles.tips}>{t("footer.subscribeDescription")}</div>
            </div>
          </div>
        </div>
      </div>
      <div className={styles["footer-ad-wrap"]}>
        <div className={styles.w}>
          <div className={styles["footer-ad"]}>
            <div className={styles.download}>
              <div className={styles.title}>{t("footer.downloadAPP")}</div>
              <div className={styles.logo}>
                <a
                  href={googleDownload}
                  target="_self"
                  exptag={"exp|" + EXPOSURE_RECORD.Footer_AppStore_Expo}
                  data-exptag-config='{"useClick": false, "stay_duration": 0, "repeated":false, "area_rate": 0.01}'
                  onClick={() => {
                    sendClickTrack.Footer_AppStore({ button_name: "googleplay" });
                  }}
                >
                  <img width="84" height="28" src="//st.joy-sourcing.com/website/home/googleplay.png?response-cache-control=max-age%3D31536000" alt="googleplay" />
                </a>
                <a
                  href={AppDownload}
                  target="_self"
                  exptag={"exp|" + EXPOSURE_RECORD.Footer_AppStore_Expo}
                  data-exptag-config='{"useClick": false, "stay_duration": 0, "repeated":false, "area_rate": 0.01}'
                  onClick={() => {
                    sendClickTrack.Footer_AppStore({ button_name: "appstore" });
                  }}
                >
                  <img width="84" height="28" src="//st.joy-sourcing.com/website/home/appStore.png?response-cache-control=max-age%3D31536000" alt="appstore" />
                </a>
              </div>
            </div>
            {/* <div className={styles["find-us"]}>
              <div className={styles.title}>{t("footer.findUsOn")}</div>
              <div className={styles.findLogo}>
                <a
                  href={Instagram}
                  target="_self"
                  aria-label={"Instagram"}
                  exptag={"exp|" + EXPOSURE_RECORD.Footer_Social_Media_Expo}
                  data-exptag-config='{"useClick": false, "stay_duration": 0, "repeated":false, "area_rate": 0.01}'
                  onClick={() => {
                    sendClickTrack.Footer_Social_Media({ button_name: "Instagram" });
                  }}
                >
                  <BrandInstagram />
                </a>
                <a
                  href={Tiktok}
                  target="_self"
                  aria-label={"Tiktok"}
                  exptag={"exp|" + EXPOSURE_RECORD.Footer_Social_Media_Expo}
                  data-exptag-config='{"useClick": false, "stay_duration": 0, "repeated":false, "area_rate": 0.01}'
                  onClick={() => {
                    sendClickTrack.Footer_Social_Media({ button_name: "Tiktok" });
                  }}
                >
                  <BrandTiktok />
                </a>
                <a
                  href={Facebook}
                  target="_self"
                  aria-label={"Facebook"}
                  exptag={"exp|" + EXPOSURE_RECORD.Footer_Social_Media_Expo}
                  data-exptag-config='{"useClick": false, "stay_duration": 0, "repeated":false, "area_rate": 0.01}'
                  onClick={() => {
                    sendClickTrack.Footer_Social_Media({ button_name: "Facebook" });
                  }}
                >
                  <BrandFacebook />
                </a>
                <a
                  href={Youtube}
                  target="_self"
                  aria-label={"Youtube"}
                  exptag={"exp|" + EXPOSURE_RECORD.Footer_Social_Media_Expo}
                  data-exptag-config='{"useClick": false, "stay_duration": 0, "repeated":false, "area_rate": 0.01}'
                  onClick={() => {
                    sendClickTrack.Footer_Social_Media({ button_name: "Youtube" });
                  }}
                >
                  <BrandYoutube />
                </a>
                <a
                  href={Linktree}
                  target="_self"
                  aria-label={"Linktree"}
                  exptag={"exp|" + EXPOSURE_RECORD.Footer_Social_Media_Expo}
                  data-exptag-config='{"useClick": false, "stay_duration": 0, "repeated":false, "area_rate": 0.01}'
                  onClick={() => {
                    sendClickTrack.Footer_Social_Media({ button_name: "Linktree" });
                  }}
                >
                  <BrandLinkTree />
                </a>
              </div>
            </div> */}
            <div className={styles["support"]}>
              <div className={styles.title}>{t("footer.paymentMethods")}</div>
              <div className={styles.logo}>
                <img width="442" height="24" src="//st.joy-sourcing.com/website/other/footer-pay-lodo.png?response-cache-control=max-age%3D31536000" alt="footer_payments" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.copyright}>
        <div className={styles.linkItemBox}>
          © 2025 Joybuy
          <div onClick={handleOpenPersonal} className={styles.linkItem}>
            {t("footer.personalisedSettings")}
          </div>
          <div onClick={handleOpenCookies} className={styles.linkItem}>
            {t("footer.manageCookies")}
          </div>
        </div>
        <div>
          This site is protected by reCAPTCHA and the Google
          <a className={styles.line2} href="https://policies.google.com/privacy">
            Privacy Policy
          </a>
          &nbsp; and
          <a className={styles.line2} href="https://policies.google.com/terms">
            Terms of Service
          </a>
          &nbsp; apply.
        </div>
      </div>
      {
        openCookiesManage && (
          <CookiesManage
            openDialog={openDialog}
            openManager={openManager}
            closeDialog={() => setOpenDialog(false)}
            closeManager={() => setOpenManager(false)}
          />
        )
      }
      {
        openPersonalManage && (
          <PersonalManage
            openDialog={openPersonalManage}
            closeDialog={() => setOpenPersonalManage(false)}
          />
        )
      }
    </footer>
  );
});

export default JDIFooter;
